import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Building2,
  Pencil,
  Trash2,
  Search,
  MoreVertical,
  MapPin,
  Globe,
  Hash,
  User,
  Users,
  Link as LinkIcon,
  Phone,
  Mail
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({ baseURL: API });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const COUNTRIES = [
  "United States",
  "Canada",
  "United Kingdom",
  "Australia",
  "Germany",
  "France",
  "India",
  "Japan",
  "Brazil",
  "Mexico",
  "Other"
];

export default function Orgs({ currentUser }) {
  const [orgs, setOrgs] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [showEditOrg, setShowEditOrg] = useState(false);
  const [showAssignUsers, setShowAssignUsers] = useState(false);
  const [showLinkedAgents, setShowLinkedAgents] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [linkedAgents, setLinkedAgents] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  const [newOrg, setNewOrg] = useState({ 
    name: "", 
    description: "",
    address: "", 
    city: "", 
    state: "", 
    postcode: "", 
    country: "United States",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    logo_url: ""
  });
  const [loading, setLoading] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ user_id: "", org_role: "org_viewer" });

  const canManageOrgs = currentUser?.role === "super_admin" || currentUser?.role === "admin";

  const fetchOrgs = async () => {
    try {
      const response = await api.get("/orgs");
      setOrgs(response.data.orgs);
    } catch (error) {
      console.error("Failed to fetch orgs:", error);
      toast.error("Failed to fetch organizations");
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data.users || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
    }
  };

  useEffect(() => {
    fetchOrgs();
    fetchUsers();
  }, []);

  const filteredOrgs = orgs.filter(org => 
    org.name?.toLowerCase().includes(search.toLowerCase()) ||
    org.account_id?.toLowerCase().includes(search.toLowerCase()) ||
    org.city?.toLowerCase().includes(search.toLowerCase()) ||
    org.state?.toLowerCase().includes(search.toLowerCase())
  );

  const createOrg = async () => {
    if (!newOrg.name) {
      toast.error("Organization name is required");
      return;
    }
    
    setLoading(true);
    try {
      await api.post("/orgs", newOrg);
      toast.success("Organization created successfully!");
      setShowCreateOrg(false);
      setNewOrg({ 
        name: "", description: "", address: "", city: "", state: "", postcode: "", 
        country: "United States", contact_name: "", contact_email: "", contact_phone: "", logo_url: ""
      });
      fetchOrgs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create organization");
    } finally {
      setLoading(false);
    }
  };

  const startEditOrg = (org) => {
    setEditingOrg({
      id: org.id,
      name: org.name || "",
      description: org.description || "",
      address: org.address || "",
      city: org.city || "",
      state: org.state || "",
      postcode: org.postcode || "",
      country: org.country || "United States",
      contact_name: org.contact_name || "",
      contact_email: org.contact_email || "",
      contact_phone: org.contact_phone || "",
      logo_url: org.logo_url || ""
    });
    setShowEditOrg(true);
  };

  const saveEditOrg = async () => {
    if (!editingOrg) return;
    
    setLoading(true);
    try {
      const updateData = {};
      Object.keys(editingOrg).forEach(key => {
        if (key !== 'id' && editingOrg[key]) {
          updateData[key] = editingOrg[key];
        }
      });
      
      await api.put(`/orgs/${editingOrg.id}`, updateData);
      toast.success("Organization updated successfully!");
      setShowEditOrg(false);
      setEditingOrg(null);
      fetchOrgs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update organization");
    } finally {
      setLoading(false);
    }
  };

  const openUserAssignment = async (org) => {
    setSelectedOrg(org);
    try {
      const response = await api.get(`/orgs/${org.id}/users`);
      setOrgUsers(response.data.users || []);
      setShowAssignUsers(true);
    } catch (error) {
      toast.error("Failed to load organization users");
    }
  };

  const assignUserToOrg = async () => {
    if (!newAssignment.user_id || !selectedOrg) {
      toast.error("Please select a user");
      return;
    }
    
    setLoading(true);
    try {
      await api.post(`/orgs/${selectedOrg.id}/users`, {
        user_id: newAssignment.user_id,
        org_id: selectedOrg.id,
        org_role: newAssignment.org_role
      });
      toast.success("User assigned successfully!");
      setNewAssignment({ user_id: "", org_role: "org_viewer" });
      // Refresh users list
      const response = await api.get(`/orgs/${selectedOrg.id}/users`);
      setOrgUsers(response.data.users || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign user");
    } finally {
      setLoading(false);
    }
  };

  const removeUserFromOrg = async (userId) => {
    if (!selectedOrg) return;
    
    try {
      await api.delete(`/orgs/${selectedOrg.id}/users/${userId}`);
      toast.success("User removed from organization");
      const response = await api.get(`/orgs/${selectedOrg.id}/users`);
      setOrgUsers(response.data.users || []);
    } catch (error) {
      toast.error("Failed to remove user");
    }
  };

  const openLinkedAgents = async (org) => {
    setSelectedOrg(org);
    try {
      const response = await api.get(`/orgs/${org.id}/linked-agents`);
      setLinkedAgents(response.data.agents || []);
      setShowLinkedAgents(true);
    } catch (error) {
      toast.error("Failed to load linked agents");
    }
  };

  const deleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Delete organization "${orgName}"? This action cannot be undone.`)) return;
    
    try {
      await api.delete(`/orgs/${orgId}`);
      toast.success("Organization deleted successfully");
      fetchOrgs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete organization");
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Organizations</h2>
          <p className="text-zinc-500 text-sm">Manage organizations and assign them to portals</p>
        </div>
        {canManageOrgs && (
          <Button onClick={() => setShowCreateOrg(true)} data-testid="create-org-btn" className="bg-white text-black hover:bg-zinc-200">
            <Plus className="w-4 h-4 mr-2" />
            Create Organization
          </Button>
        )}
      </div>

      {/* Stats Card */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-purple-950/30 border border-purple-900/50 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold">{orgs.length}</p>
              <p className="text-xs text-zinc-500">Total Organizations</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
        <Input
          placeholder="Search by name, account ID, city..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 bg-zinc-900 border-zinc-800"
          data-testid="org-search-input"
        />
      </div>

      {/* Organizations Table */}
      <Card className="bg-zinc-900 border-zinc-800">
        <CardHeader className="border-b border-zinc-800 py-3">
          <CardTitle className="text-sm font-medium text-zinc-400">
            All Organizations ({filteredOrgs.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-900/50 border-b border-zinc-800">
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Organization</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Account ID</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Location</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Portals</TableHead>
                <TableHead className="text-xs uppercase text-zinc-500 py-3">Created</TableHead>
                {canManageOrgs && (
                  <TableHead className="text-xs uppercase text-zinc-500 py-3 text-right">Actions</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredOrgs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canManageOrgs ? 6 : 5} className="text-center py-12 text-zinc-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                    <p className="text-sm">No organizations found</p>
                    {canManageOrgs && (
                      <Button 
                        onClick={() => setShowCreateOrg(true)} 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 bg-zinc-800 border-zinc-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Create Organization
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                filteredOrgs.map((org) => (
                  <TableRow key={org.id} className="border-b border-zinc-800/50 hover:bg-zinc-900/50">
                    <TableCell className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-md bg-purple-950/30 border border-purple-900/50 flex items-center justify-center">
                          <Building2 className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{org.name}</p>
                          {org.address && (
                            <p className="text-xs text-zinc-500 truncate max-w-[200px]">{org.address}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge variant="outline" className="bg-zinc-800 text-zinc-300 border-zinc-700 font-mono text-xs">
                        <Hash className="w-3 h-3 mr-1" />
                        {org.account_id}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3">
                      <div className="text-sm">
                        {org.city || org.state ? (
                          <span className="text-zinc-300">
                            {[org.city, org.state].filter(Boolean).join(", ")}
                          </span>
                        ) : (
                          <span className="text-zinc-600">-</span>
                        )}
                        {org.country && (
                          <p className="text-xs text-zinc-500 flex items-center gap-1">
                            <Globe className="w-3 h-3" />
                            {org.country}
                          </p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-3">
                      <Badge variant="outline" className="bg-emerald-950/30 text-emerald-400 border-emerald-900/50 text-xs">
                        {org.portal_count || 0} portals
                      </Badge>
                    </TableCell>
                    <TableCell className="py-3 text-sm text-zinc-500">
                      {new Date(org.created_at).toLocaleDateString()}
                    </TableCell>
                    {canManageOrgs && (
                      <TableCell className="py-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`org-menu-${org.id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                            <DropdownMenuItem onClick={() => startEditOrg(org)} className="cursor-pointer">
                              <Pencil className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openUserAssignment(org)} className="cursor-pointer">
                              <User className="w-4 h-4 mr-2" />
                              Manage Users
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => openLinkedAgents(org)} className="cursor-pointer">
                              <Building2 className="w-4 h-4 mr-2" />
                              Linked Agents
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => deleteOrg(org.id, org.name)} 
                              className="text-red-400 cursor-pointer"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Organization Dialog */}
      <Dialog open={showCreateOrg} onOpenChange={setShowCreateOrg}>
        <DialogContent className="max-w-md bg-zinc-900 border-zinc-800" data-testid="create-org-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Create Organization</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Add a new organization to assign to portals
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-zinc-400">Organization Name *</Label>
              <Input
                value={newOrg.name}
                onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                placeholder="e.g., Acme Corporation"
                className="bg-zinc-800 border-zinc-700 mt-1"
                data-testid="new-org-name"
              />
            </div>
            <div>
              <Label className="text-zinc-400">Address</Label>
              <Input
                value={newOrg.address}
                onChange={(e) => setNewOrg({ ...newOrg, address: e.target.value })}
                placeholder="123 Main Street"
                className="bg-zinc-800 border-zinc-700 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">City</Label>
                <Input
                  value={newOrg.city}
                  onChange={(e) => setNewOrg({ ...newOrg, city: e.target.value })}
                  placeholder="New York"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">State/Province</Label>
                <Input
                  value={newOrg.state}
                  onChange={(e) => setNewOrg({ ...newOrg, state: e.target.value })}
                  placeholder="NY"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-zinc-400">Postcode</Label>
                <Input
                  value={newOrg.postcode}
                  onChange={(e) => setNewOrg({ ...newOrg, postcode: e.target.value })}
                  placeholder="10001"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Country</Label>
                <Select value={newOrg.country} onValueChange={(value) => setNewOrg({ ...newOrg, country: value })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {COUNTRIES.map(country => (
                      <SelectItem key={country} value={country} className="cursor-pointer">
                        {country}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowCreateOrg(false)} className="bg-zinc-800 border-zinc-700">
              Cancel
            </Button>
            <Button onClick={createOrg} disabled={loading} className="bg-white text-black hover:bg-zinc-200" data-testid="create-org-submit">
              {loading ? "Creating..." : "Create Organization"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Organization Dialog */}
      <Dialog open={showEditOrg} onOpenChange={setShowEditOrg}>
        <DialogContent className="max-w-md bg-zinc-900 border-zinc-800" data-testid="edit-org-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Edit Organization</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Update organization details
            </DialogDescription>
          </DialogHeader>
          {editingOrg && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-zinc-400">Organization Name</Label>
                <Input
                  value={editingOrg.name}
                  onChange={(e) => setEditingOrg({ ...editingOrg, name: e.target.value })}
                  placeholder="e.g., Acme Corporation"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                  data-testid="edit-org-name"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Address</Label>
                <Input
                  value={editingOrg.address}
                  onChange={(e) => setEditingOrg({ ...editingOrg, address: e.target.value })}
                  placeholder="123 Main Street"
                  className="bg-zinc-800 border-zinc-700 mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400">City</Label>
                  <Input
                    value={editingOrg.city}
                    onChange={(e) => setEditingOrg({ ...editingOrg, city: e.target.value })}
                    placeholder="New York"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">State/Province</Label>
                  <Input
                    value={editingOrg.state}
                    onChange={(e) => setEditingOrg({ ...editingOrg, state: e.target.value })}
                    placeholder="NY"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400">Postcode</Label>
                  <Input
                    value={editingOrg.postcode}
                    onChange={(e) => setEditingOrg({ ...editingOrg, postcode: e.target.value })}
                    placeholder="10001"
                    className="bg-zinc-800 border-zinc-700 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Country</Label>
                  <Select value={editingOrg.country} onValueChange={(value) => setEditingOrg({ ...editingOrg, country: value })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {COUNTRIES.map(country => (
                        <SelectItem key={country} value={country} className="cursor-pointer">
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditOrg(false)} className="bg-zinc-800 border-zinc-700">
              Cancel
            </Button>
            <Button onClick={saveEditOrg} disabled={loading} className="bg-white text-black hover:bg-zinc-200" data-testid="edit-org-submit">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Users Dialog */}
      <Dialog open={showAssignUsers} onOpenChange={setShowAssignUsers}>
        <DialogContent className="max-w-lg bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Manage Users - {selectedOrg?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Assign users to this organization with specific roles
            </DialogDescription>
          </DialogHeader>
          
          {/* Add new user assignment */}
          <div className="border border-zinc-800 rounded-lg p-4 space-y-3">
            <Label className="text-zinc-400">Assign New User</Label>
            <div className="flex gap-2">
              <Select value={newAssignment.user_id} onValueChange={(v) => setNewAssignment({...newAssignment, user_id: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 flex-1">
                  <SelectValue placeholder="Select user" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {users.filter(u => !orgUsers.some(ou => ou.user_id === u.id)).map(user => (
                    <SelectItem key={user.id} value={user.id} className="cursor-pointer">
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={newAssignment.org_role} onValueChange={(v) => setNewAssignment({...newAssignment, org_role: v})}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 w-36">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  <SelectItem value="org_admin" className="cursor-pointer">Org Admin</SelectItem>
                  <SelectItem value="org_editor" className="cursor-pointer">Org Editor</SelectItem>
                  <SelectItem value="org_viewer" className="cursor-pointer">Org Viewer</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={assignUserToOrg} disabled={loading || !newAssignment.user_id} className="bg-white text-black hover:bg-zinc-200">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Existing users */}
          <div className="space-y-2 max-h-64 overflow-y-auto">
            <Label className="text-zinc-400">Assigned Users ({orgUsers.length})</Label>
            {orgUsers.length === 0 ? (
              <p className="text-zinc-500 text-sm py-4 text-center">No users assigned yet</p>
            ) : (
              orgUsers.map((assignment) => (
                <div key={assignment.user_id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-purple-950/30 border border-purple-900/50 flex items-center justify-center">
                      <User className="w-4 h-4 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{assignment.user?.name || "Unknown"}</p>
                      <p className="text-xs text-zinc-500">{assignment.user?.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="bg-zinc-700 text-zinc-300 border-zinc-600 text-xs">
                      {assignment.org_role?.replace('org_', '')}
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => removeUserFromOrg(assignment.user_id)} className="text-red-400 hover:text-red-300 h-8 w-8 p-0">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignUsers(false)} className="bg-zinc-800 border-zinc-700">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Linked Agents Dialog */}
      <Dialog open={showLinkedAgents} onOpenChange={setShowLinkedAgents}>
        <DialogContent className="max-w-lg bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 flex items-center gap-2">
              <LinkIcon className="w-5 h-5" />
              Linked Agents - {selectedOrg?.name}
            </DialogTitle>
            <DialogDescription className="text-zinc-500">
              Agents linked to this organization. Org users can view data from these agents.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {linkedAgents.length === 0 ? (
              <div className="text-center py-8">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-20" />
                <p className="text-zinc-500 text-sm">No agents linked yet</p>
                <p className="text-zinc-600 text-xs mt-1">Link agents from the Portal - Agent settings</p>
              </div>
            ) : (
              linkedAgents.map((agent) => (
                <div key={agent.id} className="flex items-center justify-between p-3 bg-zinc-800 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-950/30 border border-emerald-900/50 flex items-center justify-center">
                      <Building2 className="w-4 h-4 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{agent.org_name}</p>
                      <p className="text-xs text-zinc-500">{agent.portal_name} - {agent.email}</p>
                    </div>
                  </div>
                  <Badge variant="outline" className="bg-zinc-700 text-zinc-300 border-zinc-600 text-xs">
                    {agent.bills_status || "idle"}
                  </Badge>
                </div>
              ))
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkedAgents(false)} className="bg-zinc-800 border-zinc-700">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
